import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import {
  TrendingUp, Users, DollarSign, Music, Clock, Eye, BarChart3, Zap
} from "lucide-react";

const AnalyticsDashboard = ({ token }) => {
  const [analytics, setAnalytics] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [analyticsRes, trafficRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/analytics/traffic`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setAnalytics(analyticsRes.data);
      setTraffic(trafficRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="spinner" /></div>;
  if (!analytics) return <div className="card p-12 text-center"><p className="text-[#8A92A0]">Create an artist profile to see analytics.</p></div>;

  const maxHourly = Math.max(...(traffic?.hourly || []).map(h => h.views), 1);

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      <h2 className="font-['Unbounded'] text-2xl font-bold text-[#E8F0FA]">Analytics Command Center</h2>

      {/* Revenue Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total Revenue (90%)", value: `$${analytics.revenue.total.toLocaleString()}`, icon: DollarSign, color: "#32D583" },
          { label: "Last 30 Days", value: `$${analytics.revenue.last_30d.toLocaleString()}`, icon: TrendingUp, color: "#60A5FA" },
          { label: "Total Followers", value: analytics.fans.total_followers, icon: Users, color: "#C084FC" },
          { label: "Content Plays", value: analytics.content.total_plays.toLocaleString(), icon: Music, color: "#F87171" }
        ].map((stat, i) => (
          <div key={i} className="card p-5" data-testid={`stat-${i}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${stat.color}15` }}>
                <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
              </div>
              <span className="text-sm text-[#8A92A0]">{stat.label}</span>
            </div>
            <p className="text-2xl font-bold text-[#E8F0FA]">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Revenue by Type */}
      <div className="card p-6">
        <h3 className="font-semibold text-[#E8F0FA] mb-4 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#EC008C]" /> Revenue Breakdown</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Object.entries(analytics.revenue.by_type || {}).map(([type, amount]) => (
            <div key={type} className="bg-[#050810] rounded-lg p-3 border border-[#1A2030]">
              <p className="text-xs text-[#8A92A0] mb-1">{type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
              <p className="text-lg font-bold text-[#EC008C]">${amount.toLocaleString()}</p>
            </div>
          ))}
          {Object.keys(analytics.revenue.by_type || {}).length === 0 && <p className="text-[#8A92A0] text-sm col-span-3">No revenue data yet</p>}
        </div>
      </div>

      {/* Engagement */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card p-5 text-center">
          <Zap className="w-6 h-6 text-[#FF6BC5] mx-auto mb-2" />
          <p className="text-2xl font-bold text-[#E8F0FA]">{analytics.engagement.paid_interactions}</p>
          <p className="text-xs text-[#8A92A0]">Paid Messages</p>
        </div>
        <div className="card p-5 text-center">
          <Eye className="w-6 h-6 text-[#EC008C] mx-auto mb-2" />
          <p className="text-2xl font-bold text-[#E8F0FA]">{analytics.engagement.collectibles_sold}</p>
          <p className="text-xs text-[#8A92A0]">Collectibles Sold</p>
        </div>
        <div className="card p-5 text-center">
          <Users className="w-6 h-6 text-[#8DB87A] mx-auto mb-2" />
          <p className="text-2xl font-bold text-[#E8F0FA]">{analytics.engagement.experiences_booked}</p>
          <p className="text-xs text-[#8A92A0]">Experiences Booked</p>
        </div>
      </div>

      {/* Traffic Analytics */}
      {traffic && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-[#E8F0FA] flex items-center gap-2"><Eye className="w-4 h-4 text-[#EC008C]" /> Traffic Analytics</h3>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-[#8A92A0]">Today: <span className="text-[#E8F0FA] font-bold">{traffic.views_today}</span></span>
              <span className="text-[#8A92A0]">7d: <span className="text-[#E8F0FA] font-bold">{traffic.views_7d}</span></span>
              <span className="text-[#8A92A0]">30d: <span className="text-[#E8F0FA] font-bold">{traffic.views_30d}</span></span>
            </div>
          </div>

          {/* Peak Hour */}
          {traffic.peak_hour && traffic.peak_hour.views > 0 && (
            <div className="bg-[#EC008C]/10 border border-[#EC008C]/20 rounded-lg p-3 mb-4 flex items-center gap-3">
              <Clock className="w-5 h-5 text-[#EC008C]" />
              <p className="text-sm text-[#E8F0FA]">Peak hour: <span className="font-bold text-[#EC008C]">{traffic.peak_hour.label}</span> with <span className="font-bold">{traffic.peak_hour.views}</span> views</p>
            </div>
          )}

          {/* Hourly Chart */}
          <p className="text-xs text-[#8A92A0] mb-2">Views by hour (last 30 days)</p>
          <div className="flex items-end gap-[3px] h-32">
            {(traffic.hourly || []).map((h) => (
              <div key={h.hour} className="flex-1 flex flex-col items-center gap-1 group relative">
                <div
                  className="w-full rounded-t transition-all duration-300 group-hover:opacity-80"
                  style={{
                    height: `${Math.max((h.views / maxHourly) * 100, 2)}%`,
                    background: h.hour === traffic.peak_hour?.hour ? '#32D583' : '#293B35'
                  }}
                />
                <span className="text-[8px] text-[#8A92A0]">{h.hour % 6 === 0 ? h.label : ''}</span>
                {/* Tooltip */}
                <div className="absolute bottom-full mb-1 hidden group-hover:block bg-[#0A0E18] border border-[#1A2030] rounded px-2 py-1 text-[10px] text-[#E8F0FA] whitespace-nowrap z-10">
                  {h.label}: {h.views} views
                </div>
              </div>
            ))}
          </div>

          {/* Daily Chart */}
          {traffic.daily && traffic.daily.length > 0 && (
            <>
              <p className="text-xs text-[#8A92A0] mt-6 mb-2">Daily views (last 30 days)</p>
              <div className="flex items-end gap-[2px] h-20">
                {traffic.daily.map((d, i) => {
                  const maxDaily = Math.max(...traffic.daily.map(x => x.views), 1);
                  return (
                    <div key={i} className="flex-1 group relative">
                      <div
                        className="w-full rounded-t bg-[#FF6BC5] transition-all group-hover:bg-[#EC008C]"
                        style={{ height: `${Math.max((d.views / maxDaily) * 100, 3)}%` }}
                      />
                      <div className="absolute bottom-full mb-1 hidden group-hover:block bg-[#0A0E18] border border-[#1A2030] rounded px-2 py-1 text-[10px] text-[#E8F0FA] whitespace-nowrap z-10">
                        {d.date}: {d.views}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
